// src/config/t4rConfig.js
import InputIcon from "@mui/icons-material/Input";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OutputIcon from "@mui/icons-material/Output";

export const STEPS = [
  "Onboarding",
  "Organisational Goals",
  "Learner Profile",
  "Future Roles",
  "Current Knowledge",
  "Suggested MLUs",
  "Finalise MLUs",
  "Assemble Journey",
];

export const ROLE_OPTIONS = [
  // LEARN
  "Enabler of Data and Modeling Literacy",
  "Explorer of New Methods and Innovation Pathways",
  "Facilitator of Co-Creative Learning Processes",

  // TRUST
  "Guardian of Digital Responsibility",
  "Advocate for Inclusive Participation",
  "Translator Between the Public and the Model World",

  // SUSTAIN
  "Connector of Urban and Regional Systems",
  "Designer of Climate Adaptation and Resilience",
  "Pathfinder for Governance-Ready Insights",

  // BUILD
  "Designer of the Digital Twin Infrastructure",
  "Modeler of Spatial and Dynamic Processes",
  "Responsible Lead for Data Quality and Interoperability",
];

/**
 * Concepts for the “concepts” table in the LLM processing card
 * – step-specific.
 */
export const LLM_PROCESSING_CONCEPTS_STEP_5 = [
  {
    name: "Full T4R Train the Trainer MLU catalogue",
    description:
      "The details, such as MLU Title, EQF and Learning Outcome, of the MLU catalogue, designed by K8, covering Education, Governance, Technical Design, and Ethics, Inclusion & Democratisation frameworks.",
  },
  {
    name: "Triple-loop learning",
    description:
      "Connects practice (single loop), strategy (double loop), and underlying assumptions (triple loop) to prioritise MLUs that shift how the organisation learns.",
  },
  {
    name: "Co-agency model",
    description:
      "Frames how learners, teams, and organisations share responsibility across Learn–Trust–Build–Sustain roles in Local Digital Twin projects.",
  },
  {
    name: "T4R framework definitions",
    description:
      "Uses the Education, Governance, Technical Design, and Ethics & Inclusion frameworks to structure which MLUs are suggested for which perspective.",
  },
  {
    name: "Twin4Resilience project definition",
    description:
      "Anchors the suggestions in the overall goals and design principles of the Twin4Resilience programme.",
  },
  {
    name: "Co-Creation Model (Lab\u2013Studio\u2013Arena\u2013Agora)",
    description:
      "As a Stakeholder Engagement model, it considers where learning happens: experimentation (Lab), co-design (Studio), organisational practice (Arena), and public dialogue (Agora).",
  },
];


export const LLM_PROCESSING_CONCEPTS_STEP_7 = [
  {
    name: "Full T4R Train the Trainer MLU catalogue",
    description:
      "The details, such as MLU Title, EQF and Learning Outcome, of the MLU catalogue, designed by K8, covering Education, Governance, Technical Design, and Ethics, Inclusion & Democratisation frameworks.",
  },
  {
    name: "Teaching methods library",
    description:
      "Methods from the teaching methods file are combined into concrete weekly structures that fit the learner and organisational context.",
  },
  {
    name: "Triple-loop learning",
    description:
      "Connects practice (single loop), strategy (double loop), and underlying assumptions (triple loop) to prioritise MLUs that shift how the organisation learns.",
  },
  {
    name: "Co-agency model",
    description:
      "Frames how learners, teams, and organisations share responsibility across Learn–Trust–Build–Sustain roles in Local Digital Twin projects.",
  },
  {
    name: "T4R framework definitions",
    description:
      "Uses the Education, Governance, Technical Design, and Ethics & Inclusion frameworks to structure which MLUs are suggested for which perspective.",
  },
  {
    name: "Twin4Resilience project definition",
    description:
      "Anchors the suggestions in the overall goals and design principles of the Twin4Resilience programme.",
  },
  {
    name: "Co-Creation Model (Lab–Studio–Arena–Agora)",
    description:
      "As a Stakeholder Engagement model, it considers where learning happens: experimentation (Lab), co-design (Studio), organisational practice (Arena), and public dialogue (Agora).",
  },
];


export const LLM_DISCLAIMER_TEXT =
  "AI-generated suggestions may contain errors. " +
  "Review all outputs with your professional judgement. You remain responsible for the final learning journey.";

/**
 * GENERIC LLM FLOW CONFIG
 * Used as a fallback/default in LlmProcessingCard when no step-specific
 * flowBlocks / flowDetailsConfig are passed.
 */

export const LLM_GENERIC_FLOW_BLOCKS = [
  {
    id: "inputs",
    label: "Inputs",
    icon: <InputIcon fontSize="small" />,
    clickable: true,
    phase: "input",
    subunits: [
      { id: "userInputs", label: "User inputs" },
      { id: "conceptInputs", label: "Concepts & frameworks" },
    ],
  },
  {
    id: "agentPrompt",
    label: "T4R LJD Agent – Prompt Builder",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "llm",
    label: "LLM (Mistral)",
    icon: <AutoAwesomeIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "agentOutput",
    label: "T4R LJD Agent – Output Formatter",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "outputs",
    label: "Outputs",
    icon: <OutputIcon fontSize="small" />,
    clickable: true,
    phase: "output",
    subunits: [
      { id: "screenOutputs", label: "On-screen suggestions" },
      { id: "downloadable", label: "Downloadable artefacts" },
    ],
  },
];

const GENERIC_USER_INPUTS = [
  "Organisational goals & learner profile",
  "Future roles and expected contribution",
  "Current knowledge per framework",
  "MLU selections and notes",
];

const GENERIC_CONCEPT_INPUTS = [
  "T4R frameworks (EDU, GOV, TEC, EID)",
  "Triple-loop learning & co-agency",
  "Twin4Resilience project context",
];

const GENERIC_OUTPUTS = [
  "Structured suggestions on screen",
  "Markdown / text artefacts you can download and edit",
];

export const LLM_GENERIC_FLOW_DETAILS_CONFIG = {
  inputs: {
    _self: {
      title: "Inputs to the T4R Learning Journey Designer Agent",
      description:
        "The K8 Learning Journey Designer Agent combines your entries from the previous steps with key conceptual frameworks from Twin4Resilience.",
    },
    userInputs: {
      title: "User inputs",
      description:
        "Information you enter about organisational goals, the learner, future roles, current knowledge, and your curated MLU selections.",
      items: GENERIC_USER_INPUTS,
    },
    conceptInputs: {
      title: "Conceptual inputs",
      description:
        "Underlying frameworks and models that help the Agent interpret your context and structure the prompt for the LLM.",
      items: GENERIC_CONCEPT_INPUTS,
    },
  },
  agentPrompt: {
    _self: {
      title: "T4R LJD Agent – Prompt Builder",
      description:
        "The Agent turns your inputs into a structured, context-rich prompt for the Large Language Model.",
    },
  },
  llm: {
    _self: {
      title: "Large Language Model (Mistral)",
      description:
        "A compact yet capable LLM processes the structured prompt and generates draft suggestions for the learning journey.",
    },
  },
  agentOutput: {
    _self: {
      title: "T4R LJD Agent – Output Formatter",
      description:
        "The Agent parses the raw LLM output and converts it into structured views and downloadable artefacts instead of free-form text.",
    },
  },
  outputs: {
    _self: {
      title: "Outputs from the T4R LJD Agent",
      description:
        "What you see in the app and what you can export for further work with colleagues.",
      items: GENERIC_OUTPUTS,
    },
    screenOutputs: {
      title: "On-screen suggestions",
      description:
        "Interactive views that allow you to review, edit, and curate suggestions per step.",
    },
    downloadable: {
      title: "Downloadable artefacts",
      description:
        "Markdown or text files that you can store, adapt, and share as part of your course design documentation.",
    },
  },
};

/**
 * STEP 5 – LLM flow config
 * (Suggested MLUs)
 */

const STEP5_USER_INPUTS = [
  "Step 1 – Organisational goals",
  "Step 2 – Learner profile",
  "Step 3 – Future roles & contribution",
  "Step 4 – Current knowledge per framework",
];

const STEP5_CONCEPT_INPUTS = [
  "Full MLU catalog of Learning Outcomes (EDU, GOV, TEC, EID)",
  "Triple-loop learning",
  "Co-agency model",
  "T4R frameworks & modules",
  "Twin4Resilience project context",
  "Lab–Studio–Arena–Agora setup",
];

const STEP5_OUTPUTS = ["Suggested MLUs", "Why these MLUs?"];

export const STEP5_LLM_FLOW_BLOCKS = [
  {
    id: "inputs",
    label: "Inputs",
    icon: <InputIcon fontSize="small" />,
    clickable: true,
    phase: "input",
    subunits: [
      { id: "userInputs", label: "User inputs" },
      { id: "conceptInputs", label: "Concepts" },
    ],
  },
  {
    id: "agentPrompt",
    label: "T4R LJD Agent – Input Processor",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "llm",
    label: "LLM (Mistral)",
    icon: <AutoAwesomeIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "agentOutput",
    label: "T4R LJD Agent – Output Processor",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "outputs",
    label: "Outputs",
    icon: <OutputIcon fontSize="small" />,
    clickable: true,
    phase: "output",
    subunits: [
      { id: "suggestedMlus", label: "Suggested MLUs" },
      { id: "explanations", label: "Why these MLUs?" },
    ],
  },
];

export const STEP5_LLM_FLOW_DETAILS_CONFIG = {
  inputs: {
    _self: {
      title: "Inputs to the T4R LJD Agent (Step 5)",
      description:
        "All information you provide in the previous steps, plus key conceptual frameworks, are used as inputs to the K8 Learning Journey Designer Agent.",
    },
    userInputs: {
      title: "User inputs (from Steps 1–4)",
      description:
        "These are the concrete entries you provide in the Learning Journey Designer. They anchor the recommendations in your real organisational and learner context.",
      items: STEP5_USER_INPUTS,
    },
    conceptInputs: {
      title: "Conceptual inputs (frameworks & models)",
      description:
        "These background concepts help the T4R LJD Agent interpret your context and phrase a better LLM prompt.",
      items: STEP5_CONCEPT_INPUTS,
    },
  },
  agentPrompt: {
    _self: {
      title: "T4R LJD Agent – Input Processor",
      description:
        "The K8 designed T4R LJD Agent combines user inputs and conceptual inputs into a coherent, structured prompt. It highlights goals, roles, current knowledge, and key T4R concepts before calling the Large Language Model.",
    },
  },
  llm: {
    _self: {
      title: "Large Language Model (Mistral)",
      description:
        "The Mistral-based LLM receives the structured prompt from the K8 designed T4R LJD Agent and generates draft suggestions for future learning. We use a compact yet capable model to balance quality, speed, and responsible resource use.",
    },
  },
  agentOutput: {
    _self: {
      title: "T4R LJD Agent – Output Processor",
      description:
        "The K8 designed T4R LJD Agent parses the raw LLM output, extracts relevant elements, and maps them back to the Twin4Resilience frameworks so that you see structured results instead of free-form text.",
    },
  },
  outputs: {
    _self: {
      title: "Outputs shown in Step 5 & Step 6",
      description:
        "These are the structured results you see as suggested microlearning units and explanations.",
      items: STEP5_OUTPUTS,
    },
    suggestedMlus: {
      title: "Suggested MLUs per framework",
      description:
        "The system proposes specific microlearning units in the Education, Governance, Technical Design, and Ethics frameworks that match the learner and organisational context.",
    },
    explanations: {
      title: "Explanations for each suggestion",
      description:
        "For each suggested MLU, the agent provides a short explanation of why it fits – for example which goal, role, or knowledge gap it responds to.",
    },
  },
};

/**
 * STEP 7 – LLM flow config
 * (Assemble Journey)
 */

export const STEP7_LLM_USER_INPUTS = [
  "Step 1 – Organisational goals",
  "Step 2 – Learner profile",
  "Step 3 – Future roles & expected contribution",
  "Step 4 – Current knowledge per framework",
  "Step 6 – Curated MLUs per framework",
  "Step 6 – Designer notes on goals, fit, and follow-ups",
];

export const STEP7_LLM_CONCEPT_INPUTS = [
  "Teaching methods library",
  "Triple-loop learning",
  "Co-agency model",
  "T4R frameworks & modules",
  "Twin4Resilience project context",
  "Lab–Studio–Arena–Agora setup",
];

export const STEP7_LLM_OUTPUTS = [
  "Short overview paragraph of the teaching journey",
  "Markdown table: session-by-session teaching plan",
  "Columns: Session/Week, MLU(s), teaching methods, example course structure",
];

export const STEP7_LLM_FLOW_BLOCKS = [
  {
    id: "inputs",
    label: "Inputs",
    icon: <InputIcon fontSize="small" />,
    clickable: true,
    phase: "input",
    subunits: [
      { id: "userInputs", label: "User inputs" },
      { id: "conceptInputs", label: "Concepts" },
    ],
  },
  {
    id: "agentPrompt",
    label: "T4R LJD Agent – Prompt Builder",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "llm",
    label: "LLM (Mistral)",
    icon: <AutoAwesomeIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "agentOutput",
    label: "T4R LJD Agent – Teaching Plan Formatter",
    icon: <PsychologyIcon fontSize="small" />,
    clickable: true,
    phase: "ai",
  },
  {
    id: "outputs",
    label: "Outputs",
    icon: <OutputIcon fontSize="small" />,
    clickable: true,
    phase: "output",
    subunits: [
      { id: "planIntro", label: "Teaching plan overview" },
      { id: "planTable", label: "Markdown table" },
    ],
  },
];

export const STEP7_LLM_FLOW_DETAILS_CONFIG = {
  inputs: {
    _self: {
      title: "Inputs to the T4R LJD Agent (Step 7)",
      description:
        "For Step 7, the K8 Learning Journey Designer Agent combines your earlier inputs, curated MLUs, and the teaching methods library to build a detailed teaching plan.",
    },
    userInputs: {
      title: "User inputs (from Steps 1–6)",
      description:
        "These entries come directly from your work in the previous steps and anchor the teaching plan in a real organisational and learner context.",
      items: STEP7_LLM_USER_INPUTS,
    },
    conceptInputs: {
      title: "Conceptual inputs (T4R frameworks & models)",
      description:
        "These background concepts guide how the Agent groups MLUs into sessions and selects suitable teaching methods.",
      items: STEP7_LLM_CONCEPT_INPUTS,
    },
  },
  agentPrompt: {
    _self: {
      title: "T4R LJD Agent – Prompt Builder",
      description:
        "The Agent transforms your curated MLUs, notes, and teaching methods into a structured prompt that explains the target role, current knowledge, and desired learning journey.",
    },
  },
  llm: {
    _self: {
      title: "Large Language Model (Mistral)",
      description:
        "The Mistral-based LLM receives the structured prompt and proposes a session-by-session teaching plan that moves the learner towards their future role.",
    },
  },
  agentOutput: {
    _self: {
      title: "T4R LJD Agent – Teaching Plan Formatter",
      description:
        "The Agent parses the raw LLM output and converts it into a concise Markdown overview and a table that fits the Twin4Resilience teaching format.",
    },
  },
  outputs: {
    _self: {
      title: "Outputs shown in Step 7",
      description:
        "These are the concrete artefacts you see as a teaching plan preview and can download for further editing.",
      items: STEP7_LLM_OUTPUTS,
    },
    planIntro: {
      title: "Teaching plan overview",
      description:
        "A short introductory paragraph (2–3 sentences) summarising the logic and focus of the learning journey.",
    },
    planTable: {
      title: "Session-by-session Markdown table",
      description:
        "A structured table with one row per session or week, listing MLUs, teaching methods, and example course structure.",
    },
  },
};
